import { useCallback, useRef, useState } from "react";

export interface ToastItem {
  id: number;
  tone: "success" | "error";
  message: string;
}

export interface ToastApi {
  toasts: ToastItem[];
  pushSuccess: (message: string) => void;
  pushError: (message: string) => void;
  dismiss: (id: number) => void;
}

/** Toasts efimeros para acciones rapidas del listado (role="status"). */
export function useToasts(): ToastApi {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastItem["tone"], message: string) => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((current) => [...current.slice(-2), { id, tone, message }]);
      window.setTimeout(() => dismiss(id), 6000);
    },
    [dismiss],
  );

  const pushSuccess = useCallback((message: string) => push("success", message), [push]);
  const pushError = useCallback((message: string) => push("error", message), [push]);

  return { toasts, pushSuccess, pushError, dismiss };
}

export function ToastStack({ toasts, onDismiss }: { toasts: ToastItem[]; onDismiss: (id: number) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-label="Notificaciones"
      className="fixed bottom-4 left-1/2 z-50 flex w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2 flex-col gap-2 sm:left-auto sm:right-4 sm:translate-x-0"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${
            toast.tone === "success"
              ? "border-market/30 text-ink bg-white"
              : "border-red-300 bg-red-50 text-red-800"
          }`}
        >
          <span
            aria-hidden="true"
            className={`mt-0.5 h-2.5 w-2.5 flex-none rounded-full ${
              toast.tone === "success" ? "bg-market" : "bg-red-600"
            }`}
          />
          <p className="flex-1 leading-snug">{toast.message}</p>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            aria-label="Cerrar notificacion"
            className="text-mist hover:text-ink focus-visible:ring-gold flex-none rounded p-0.5 focus-visible:outline-none focus-visible:ring-2"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ))}
    </div>
  );
}
